import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, MessagePayload, deleteToken } from "firebase/messaging";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
};

const currentToken = ref<string | null>(null);
const alreadySentToServer = ref(false);
var hasPermission = ref(false);
const incomingMessage = ref<{ title: string; body: string } | null>(null);
const alreadyRefreshedThisSession = ref(false);

/* --------------------------------------------- 
  Push notification Logic flow.
-----------------------------------------------
If Alerts feature is not enabled, no Push notification UI will be shown and no permission will be requested.
A new user will have the option to opt-out of all notifications during registration.

On every page load and user load, we check for an is-opted-in flag in local storage and in the user's profile.

If the user's profile indicates they have opted in, but local storage does not, we will ask the user if they want to enable notifications on this device.
A popup will be shown with the following options:
  1. Yes, enable notifications (will receive notifications on this device)
  2. Do not subscribe on this device (will still receive notifications on other devices)
  3. Do not subscribe on any device (will not receive notifications at all)

If the user opts in, we will request notification permission and send the generated token to the server. 
We will also set a flag in session storage indicating that the token has been sent to the server for this session.

If the user opts out on this device, we will not request notification permission
 and we will set a flag in local storage indicating that the user has manually unsubscribed on this device. 
 This will prevent future prompts on this device, but the user may still receive notifications on other devices.

If the user opts out on all devices, we will not request notification permission and we will set 
 a param in the user's profile indicating that they have unsubscribed from push notifications. 
 This will prevent future prompts on all devices and the user will not receive notifications.


 // Refreshing last-seen date:
Once per browser session, we will re-request the token from Firebase and resend it to the server, even if we already have permission and a token for this device.
 This allows us to update a "last-seen" date for this device on the server side,
  which we can use to determine active devices and clean up old tokens.
  It also confirms that the token is still valid and working, and that the browser still has permission to receive notifications setup correctly
  If not, we can prompt the user to re-enable notifications.
  
 */

export function useFirebase() {
  // Initialize Firebase
  const app = initializeApp(firebaseConfig);
  const messaging = getMessaging(app);
  const ua = navigator.userAgent.toLowerCase();
  const media = window.matchMedia("(display-mode: standalone)").matches;
  const isStandalone = media || navigator["standalone"];
  let browserName = "Unknown";
  let isIOS =
    (ua.match(/iPhone|iPad|iPod/i) ? true : false) ||
    ((ua.match(/Mac OS/i) ? true : false) && navigator?.maxTouchPoints > 0);
  let isAndroid = ua.match(/android/i) ? true : false;
  let isMacOS = false;
  let isWindows = false;
  let isSafari = false;
  let isEdge = false;
  let isChrome = false;
  let isFirefox = false;

  // Check browser type and platform
  if (ua.match(/edg/i)) {
    browserName = "Edge"; // Must check Edge before Chrome
    isEdge = true;
  } else if (ua.match(/chrome|chromium|crios/i)) {
    browserName = "Chrome";
    isChrome = true;
  } else if (ua.match(/android|iphone|ipad|ipod/i) && ua.match(/safari/i)) {
    browserName = "Mobile Safari"; // Mobile Safari
    isIOS = true;
    isSafari = true;
  } else if (ua.match(/firefox|fxios/i)) {
    browserName = "Firefox";
    isFirefox = true;
  } else if (ua.match(/safari/i)) {
    browserName = "Safari";
    isSafari = true;
  }
  // Check platform
  if (!isIOS && (ua.match(/mac os x/i) || ua.match(/macintosh/i)) && !navigator.maxTouchPoints) {
    isMacOS = true;
  } else if (ua.match(/windows/i)) {
    isWindows = true;
  } else if (ua.match(/android/i)) {
    isAndroid = true;
  }

  // Use `CSS.supports` in Javascript or `@supports` in CSS to check if the feature is available.
  // true on iOS 17.0+ (Safari 17.0+)
  const isiOS17plus = isIOS && CSS.supports("contain-intrinsic-size", "100px");

  // **************************************************************************
  //  TRY GETTING PERMISSION AND TOKEN
  // **************************************************************************
  //
  // Request notification permission from the user
  // If 'force' is true, will ask again even if already asked this session
  // returns null if no error, otherwise returns error message
  async function tryGetNotificationPermission(force: boolean = false): Promise<string | null> {
    if (force) {
      // If forcing, reset the "sent to server" flag so we can try again
      setTokenSentToServer(false);
      setManuallyUnsubscribed(false);
    }

    if (isManuallyUnsubscribed()) {
      console.log("User has manually unsubscribed from notifications.");
      hasPermission.value = false;
      return null;
    }

    // Check if permission is already granted
    if (Notification.permission === "granted" && isTokenSentToServer() && !force) {
      console.log("Notification permission already granted.");
      hasPermission.value = true;
      return null;
    } else {
      console.log("Requesting notification permission...");
      try {
        // Get registration token.
        // Initially this makes a network call, once retrieved
        //    subsequent calls to getToken will return from cache.
        // This will trigger a permission prompt if not already granted
        // https://firebase.google.com/docs/cloud-messaging/js/client#retrieve-the-current-registration-token
        currentToken.value = await getToken(messaging, {
          vapidKey: firebaseConfig.vapidKey,
          //serviceWorkerRegistration: await navigator.serviceWorker.ready,
        });

        if (currentToken.value) {
          console.log(
            "Notification token: ..." + currentToken.value.slice(currentToken.value.length - 8),
          );
          hasPermission.value = true;
        } else {
          // Show permission request.
          console.log("No registration token available. Request permission to generate one.");
        }
      } catch (err) {
        console.log("An error occurred while retrieving token. ", err);
        return (err as Error).message;
      }
    }
  }

  // Handle incoming messages. Called when:
  // - a message is received while the app has focus
  // - the user clicks on an app notification created by a service worker
  //   `messaging.onBackgroundMessage` handler.
  onMessage(messaging, (payload: MessagePayload) => {
    console.log(">> Firebase Message: " + payload?.notification?.title, payload);
    incomingMessage.value = {
      title: payload?.notification?.title ?? "Notification",
      body: payload?.notification?.body ?? "",
    };
  });

  // Helper to determine if the token has been sent to the server
  //   for this session
  function isTokenSentToServer() {
    return alreadySentToServer.value;
    //return window.sessionStorage.getItem("sentToServer") === "1";
  }

  // Set a value indicating whether the token has been sent to the server for this session.
  // We save value in Session storage (rather than localStorage)
  // because we want the token to be sent once per session
  //   so that we can update a "last-seen" date on the server side for this device
  function setTokenSentToServer(sent: boolean) {
    //window.sessionStorage.setItem("sentToServer", sent ? "1" : "0");
    alreadySentToServer.value = sent;
  }

  function isManuallyUnsubscribed() {
    return window.localStorage.getItem("manuallyUnsubscribed") === "1";
  }

  function setManuallyUnsubscribed(unsubscribed: boolean) {
    window.localStorage.setItem("manuallyUnsubscribed", unsubscribed ? "1" : "0");
  }

  // Unsubscribe from notifications
  async function unsubscribeNotifications() {
    try {
      if (hasPermission.value) {
        let deleteSuccess = await deleteToken(messaging);
        if (deleteSuccess) {
          console.log("Token deleted.");
          currentToken.value = null;
          hasPermission.value = false;
          setManuallyUnsubscribed(true);
        } else {
          console.log("Unable to delete token.");
        }
      } else {
        console.log("No token to delete.");
      }
    } catch (err) {
      console.log("An error occurred while deleting token. ", err);
    }
  }

  return {
    currentToken,
    hasPermission,
    incomingMessage,
    alreadyRefreshedThisSession,
    unsubscribeNotifications,
    tryGetNotificationPermission,
    isTokenSentToServer,
    setTokenSentToServer,
    isManuallyUnsubscribed,
    setManuallyUnsubscribed,
    browserName,
    isIOS,
    isAndroid,
    isMacOS,
    isWindows,
    isStandalone,
    isSafari,
    isEdge,
    isChrome,
    isFirefox,
    isiOS17plus,
  };
}
